/**
 * With Timer (Season Sale) section
 * - Countdown (epoch seconds from Liquid; shop timezone)
 * - Category pill switching between pre-rendered lists
 * - Desktop carousel arrows: scroll by 1 slide via slideshow-component API
 * - Sale ended state: "Aktion beendet" + disable CTAs (products stay visible)
 */

/** @typedef {HTMLElement & { previous?: (event?: Event, options?: any) => void, next?: (event?: Event, options?: any) => void }} SlideshowComponent */

/** @param {number} n */
function pad2(n) {
  return String(n).padStart(2, '0');
}

/** @param {number} ms */
function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${pad2(hours)} hr : ${pad2(minutes)} min : ${pad2(seconds)} sec`;
}

/** @param {ParentNode} root @param {boolean} disabled */
function setCtasDisabled(root, disabled) {
  for (const el of root.querySelectorAll('[data-with-timer-cta]')) {
    if (el instanceof HTMLButtonElement) {
      el.disabled = disabled;
      el.setAttribute('aria-disabled', String(disabled));
    } else if (el instanceof HTMLAnchorElement) {
      if (disabled) {
        el.setAttribute('aria-disabled', 'true');
        el.dataset.disabled = 'true';
      } else {
        el.removeAttribute('aria-disabled');
        delete el.dataset.disabled;
      }
    } else if (el instanceof HTMLElement) {
      // placeholder CTA div
      if (disabled) el.setAttribute('aria-disabled', 'true');
    }
  }
}

/** @param {HTMLElement} section */
function initTimer(section) {
  const timerEl = section.querySelector('[data-with-timer-timer]');
  if (!timerEl) return;

  const label = timerEl.getAttribute('data-label') || 'Ends in:';
  const endedText = timerEl.getAttribute('data-ended-text') || 'Aktion beendet';
  let endEpoch = Number(timerEl.getAttribute('data-end-epoch') || '0');
  const labelEl = timerEl.querySelector('.with-timer__timer-label');
  const valueEl = timerEl.querySelector('[data-with-timer-timer-value]');

  // Fallback: parse datetime string if epoch missing (e.g. Liquid date filter didn't parse)
  if ((!Number.isFinite(endEpoch) || endEpoch <= 0) && timerEl.getAttribute('data-end-datetime')) {
    const dateStr = timerEl.getAttribute('data-end-datetime').trim();
    if (dateStr) {
      const parsed = new Date(dateStr.replace(' ', 'T'));
      if (!Number.isNaN(parsed.getTime())) endEpoch = Math.floor(parsed.getTime() / 1000);
    }
  }

  // No end date set
  if (!Number.isFinite(endEpoch) || endEpoch <= 0) {
    if (labelEl && valueEl) {
      labelEl.textContent = label;
      valueEl.textContent = '--';
    } else {
      timerEl.textContent = `${label} --`;
    }
    return;
  }

  const endMs = endEpoch * 1000;

  const tick = () => {
    const now = Date.now();
    const remaining = endMs - now;

    if (remaining <= 0) {
      timerEl.textContent = endedText;
      section.dataset.saleEnded = 'true'; // sets data-sale-ended="true"
      setCtasDisabled(section, true);
      return false;
    }

    if (labelEl && valueEl) {
      labelEl.textContent = label;
      valueEl.textContent = formatRemaining(remaining);
    } else {
      timerEl.textContent = `${label} ${formatRemaining(remaining)}`;
    }
    return true;
  };

  // Initial render
  const shouldContinue = tick();
  if (!shouldContinue) return;

  const interval = window.setInterval(() => {
    const keepGoing = tick();
    if (!keepGoing) window.clearInterval(interval);
  }, 1000);
}

/** @param {HTMLElement} section */
function initPills(section) {
  const pills = Array.from(section.querySelectorAll('[data-with-timer-pill]'));
  const lists = Array.from(section.querySelectorAll('[data-with-timer-list]'));
  const viewMore = section.querySelector('[data-with-timer-view-more]');
  if (!pills.length || !lists.length) return;

  /** @param {string} key */
  const setActive = (key) => {
    for (const pill of pills) {
      const isActive = pill.getAttribute('data-with-timer-pill') === key;
      pill.setAttribute('aria-pressed', String(isActive));
      pill.classList.toggle('is-active', isActive);
    }

    for (const list of lists) {
      const isMatch = list.getAttribute('data-with-timer-list') === key;
      list.classList.toggle('is-active', isMatch);
      list.toggleAttribute('hidden', !isMatch);
    }

    if (viewMore instanceof HTMLAnchorElement) {
      const activePill = pills.find((p) => p.getAttribute('data-with-timer-pill') === key);
      const url = activePill?.getAttribute('data-with-timer-url') || '';
      if (url) {
        viewMore.href = url;
        viewMore.removeAttribute('aria-disabled');
        viewMore.dataset.disabled = 'false';
        viewMore.style.pointerEvents = '';
        viewMore.style.opacity = '';
      } else {
        viewMore.href = '#';
        viewMore.setAttribute('aria-disabled', 'true');
        viewMore.dataset.disabled = 'true';
        viewMore.style.pointerEvents = 'none';
        viewMore.style.opacity = '0.6';
      }
    }
  };

  const defaultKey = section.getAttribute('data-default-pill') || pills[0]?.getAttribute('data-with-timer-pill');
  if (defaultKey) setActive(defaultKey);

  for (const pill of pills) {
    pill.addEventListener('click', () => {
      const key = pill.getAttribute('data-with-timer-pill');
      if (!key) return;
      setActive(key);
      // Init carousel nav for newly visible list (lazy — skipped at boot)
      const activeList = lists.find((l) => l.getAttribute('data-with-timer-list') === key);
      if (activeList) initListNav(activeList);
    });
  }
}

/**
 * @param {HTMLElement} scrollEl
 * @param {HTMLButtonElement | null} prevBtn
 * @param {HTMLButtonElement | null} nextBtn
 */
function updateWithTimerNavDisabled(scrollEl, prevBtn, nextBtn) {
  if (!scrollEl || (!(prevBtn instanceof HTMLButtonElement) && !(nextBtn instanceof HTMLButtonElement))) return;
  const tol = 3;
  const maxScroll = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
  if (maxScroll <= tol) {
    if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = true;
    if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = true;
    return;
  }
  const dir = getComputedStyle(scrollEl).direction;
  const sl = scrollEl.scrollLeft;
  let atStart;
  let atEnd;
  if (dir === 'rtl') {
    atStart = sl >= maxScroll - tol;
    atEnd = sl <= tol;
  } else {
    atStart = sl <= tol;
    atEnd = sl >= maxScroll - tol;
  }
  if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = atStart;
  if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = atEnd;
}

/** @param {HTMLElement} list */
function initListNav(list) {
  if (!(list instanceof HTMLElement)) return;
  if (list.dataset.navInitialized === 'true') return;
  list.dataset.navInitialized = 'true';

  const prevBtn = list.querySelector('[data-with-timer-nav="prev"]');
  const nextBtn = list.querySelector('[data-with-timer-nav="next"]');
  /** @type {SlideshowComponent|null} */
  const slideshow = /** @type {any} */ (list.querySelector('slideshow-component'));

  if (!(prevBtn instanceof HTMLButtonElement) || !(nextBtn instanceof HTMLButtonElement)) return;

  const getScrollEl = () => {
    const candidates = [
      list.querySelector('[ref="scroller"]'),
      list.querySelector('slideshow-slides'),
      slideshow instanceof HTMLElement ? slideshow.querySelector('[ref="scroller"]') : null,
      slideshow instanceof HTMLElement ? slideshow.querySelector('slideshow-slides') : null,
    ];
    for (const c of candidates) {
      if (c instanceof HTMLElement) return c;
    }
    return null;
  };

  /** @param {HTMLElement} el */
  const getStep = (el) => {
    const slide =
      el.querySelector('slideshow-slide, .resource-list__slide, .resource-list__item') ||
      el.querySelector('li, .card, .product-card');
    const rect = slide ? slide.getBoundingClientRect() : null;
    const cardWidth = rect ? Math.round(rect.width) : Math.round(el.clientWidth * 0.25);
    const gapRaw =
      getComputedStyle(el).getPropertyValue('--resource-list-column-gap-desktop') ||
      getComputedStyle(el).getPropertyValue('--column-gap') ||
      '';
    const gap = gapRaw ? parseFloat(gapRaw) : 20;
    return Math.max(1, cardWidth + gap);
  };

  let navRaf = 0;
  const scheduleNavState = () => {
    if (navRaf) return;
    navRaf = requestAnimationFrame(() => {
      navRaf = 0;
      const el = getScrollEl();
      if (el) updateWithTimerNavDisabled(el, prevBtn, nextBtn);
    });
  };

  prevBtn.addEventListener('click', (e) => {
    if (prevBtn.disabled) return;
    e.preventDefault();
    if (slideshow && typeof slideshow.previous === 'function') {
      slideshow.previous(e, { animate: true });
      scheduleNavState();
      return;
    }
    const el = getScrollEl();
    if (!el) return;
    const amount = -getStep(el);
    el.scrollBy({ left: amount, behavior: 'smooth' });
  });

  nextBtn.addEventListener('click', (e) => {
    if (nextBtn.disabled) return;
    e.preventDefault();
    if (slideshow && typeof slideshow.next === 'function') {
      slideshow.next(e, { animate: true });
      scheduleNavState();
      return;
    }
    const el = getScrollEl();
    if (!el) return;
    const amount = getStep(el);
    el.scrollBy({ left: amount, behavior: 'smooth' });
  });

  const scrollEl = getScrollEl();
  if (scrollEl) {
    scrollEl.addEventListener('scroll', scheduleNavState, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(scheduleNavState).observe(scrollEl);
    }
  }
  if (slideshow instanceof HTMLElement) {
    slideshow.addEventListener('slideshow:select', scheduleNavState);
  }

  scheduleNavState();
}

/** @param {HTMLElement} section */
function initCarouselNav(section) {
  const lists = Array.from(section.querySelectorAll('[data-with-timer-list]'));
  if (!lists.length) return;

  // Only init the visible (active) list immediately — hidden lists init on pill click
  for (const list of lists) {
    if (!(list instanceof HTMLElement)) continue;
    if (!list.hidden) initListNav(list);
  }
}

/** @param {HTMLElement} section */
function initSection(section) {
  initTimer(section);
  initPills(section);
  initCarouselNav(section);

  // Prevent navigation on disabled anchor CTAs
  section.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[data-with-timer-cta][data-disabled="true"]');
    if (link) {
      e.preventDefault();
      e.stopPropagation();
    }
  });
}

function boot() {
  for (const section of document.querySelectorAll('[data-with-timer-section]')) {
    if (section instanceof HTMLElement && section.dataset.initialized !== 'true') {
      initSection(section);
      section.dataset.initialized = 'true';
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(boot, { timeout: 2000 });
    } else {
      setTimeout(boot, 200);
    }
  });
} else {
  // Already loaded (e.g. script injected late)
  boot();
}
document.addEventListener('shopify:section:load', boot);

